// assets/AllCourses/Exercises/MultipleChoiceExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MultipleChoiceExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.multipleChoiceQuestions)
    ? exercise.multipleChoiceQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = next => {
    if (isEditMode)
      onUpdate("multipleChoiceQuestions", next);
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[idx] || {
      question: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    next[idx] = { ...current, [field]: value };
    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResult) return;

    setState(prev => {
      const next = {
        answers: { ...prev.answers, [qIdx]: value },
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every(
        (_, i) => next.answers[i] !== undefined
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, idx) => {
      const user = currentState.answers[idx] || "";
      return (
        normalize(user, q.requireCapitalization) ===
        normalize(q.answer, q.requireCapitalization)
      );
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6">
            <div className="space-y-6">
              <TextareaField
                label={`Question ${qIdx + 1}`}
                value={q.question || ""}
                onChange={v =>
                  updateQuestionField(qIdx, "question", v)
                }
                placeholder="What is the capital of France?"
                rows={3}
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-mc-question-${qIdx}`
                  ]
                }
              />

              <div className="space-y-3">
                <Label>Options</Label>

                {q.options.map((opt, optIdx) => (
                  <div key={optIdx} className="flex gap-3">
                    <StringField
                      value={opt}
                      onChange={v => {
                        const next = [...questions];
                        next[qIdx].options[optIdx] = v;
                        updateQuestions(next);
                      }}
                      placeholder="Option"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        updateQuestions(
                          questions.map((qq, i) =>
                            i === qIdx
                              ? {
                                  ...qq,
                                  options: qq.options.filter(
                                    (_, oi) => oi !== optIdx
                                  ),
                                }
                              : qq
                          )
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    updateQuestions(
                      questions.map((qq, i) =>
                        i === qIdx
                          ? {
                              ...qq,
                              options: [...qq.options, ""],
                            }
                          : qq
                      )
                    )
                  }
                >
                  Add Option
                </Button>
              </div>

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={v =>
                  updateQuestionField(qIdx, "answer", v)
                }
                placeholder="Answer"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-mc-answer-${qIdx}`
                  ]
                }
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={c =>
                  updateQuestionField(
                    qIdx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== qIdx)
                  )
                }
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                question: "",
                options: [],
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No multiple-choice questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Multiple Choice
        </CardTitle>
        <CardDescription>
          Choose the correct answer for each question.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const selected = state.answers[qIdx];
          const isCorrect =
            showResult &&
            selected &&
            normalize(selected, q.requireCapitalization) ===
              normalize(q.answer, q.requireCapitalization);

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <p className="text-base md:text-lg font-medium">
                {q.question}
              </p>

              <RadioGroup
                value={selected}
                onValueChange={v =>
                  handleChange(qIdx, v)
                }
                disabled={showResult}
                className="space-y-3"
              >
                {q.options.map((opt, optIdx) => {
                  const correct =
                    normalize(opt, q.requireCapitalization) ===
                    normalize(q.answer, q.requireCapitalization);

                  const isSelected = selected === opt;

                  return (
                    <div
                      key={optIdx}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-4 transition",
                        isSelected && "bg-accent",
                        showResult &&
                          isSelected &&
                          correct &&
                          "bg-green-100 border-green-500",
                        showResult &&
                          isSelected &&
                          !correct &&
                          "bg-red-100 border-red-500"
                      )}
                    >
                      <RadioGroupItem
                        value={opt}
                        id={`mc-${qIdx}-${optIdx}`}
                      />
                      <Label
                        htmlFor={`mc-${qIdx}-${optIdx}`}
                        className="flex-1 cursor-pointer"
                      >
                        {opt}
                        {showResult && isSelected && (
                          <span className="ml-3 text-sm">
                            {correct
                              ? "Correct"
                              : `Correct: ${q.answer}`}
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
